import React, { useEffect, useState, Component } from 'react';
import { X, User, Building2, Users, CheckCircle2, Mail } from 'lucide-react';
import confetti from 'canvas-confetti';
import { Button } from './ui/Button';
const SUPPORT_EMAIL = 'support@zanoplatform.com';
// Web3Forms delivers submissions straight to your inbox — no email client.
// Get a free access key at https://web3forms.com and set its destination to
// support@zanoplatform.com, then paste the key below.
const WEB3FORMS_ACCESS_KEY = 'daa0e880-90c5-42b7-911c-ac1a7e0e4820';
export const AgencyModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referred, setReferred] = useState('');
  // Reset back to the form whenever the modal is fully closed.
  useEffect(() => {
    if (!isOpen) {
      const t = window.setTimeout(() => {
        setSubmitted(false);
        setError(null);
        setSubmitting(false);
        setReferred('');
      }, 250);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zano-pink focus:border-zano-pink outline-none text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const rainConfetti = () => {
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ['#EC4899', '#F9A8D4', '#C4B5FD', '#38BDF8', '#FBCFE8'];
    // Initial celebratory burst
    confetti({
      particleCount: 120,
      spread: 90,
      origin: {
        y: 0.3
      },
      colors
    });
    // Continuous rain from the top
    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 90,
        spread: 70,
        startVelocity: 35,
        origin: {
          x: Math.random(),
          y: -0.1
        },
        gravity: 0.8,
        colors
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const data = new FormData(e.currentTarget);
    const get = (k: string) => data.get(k) as string || '—';
    // Send the submission straight to support@zanoplatform.com via Web3Forms.
    const message = ['ABOUT YOU', `Full name: ${get('fullName')}`, `Role / title: ${get('role')}`, `Email: ${get('email')}`, `LinkedIn profile URL: ${get('linkedin')}`, '', 'YOUR AGENCY', `Agency name: ${get('agencyName')}`, `Website: ${get('website')}`, `Number of creators: ${get('creators')}`, '', 'YOUR CREATORS', `Roster link: ${get('rosterLink')}`, `Primary niche: ${get('niche')}`, `Notes: ${get('notes')}`, '', 'REFERRAL', `Referred by an existing Zano user?: ${get('referred')}`, `Referred by: ${get('referredBy')}`].join('\n');
    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: 'New agency listing request',
      from_name: 'Zano — List Your Agency form',
      replyto: get('email'),
      message
    };
    try {
      setSubmitting(true);
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || 'Submission failed');
      }
      rainConfetti();
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong sending your request. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-29">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden relative max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-30">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10" data-id="element-31">
          <X size={20} data-id="element-32" />
        </button>

        {submitted ? <div className="p-8 sm:p-10 text-center" data-id="element-33">
            <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50" data-id="element-34">
              <CheckCircle2 size={32} className="text-emerald-500" data-id="element-35" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-36">
              Request received!
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-37">
              Our team will review your request and respond within 48 hours. If
              approved, you'll receive an invite to the marketplace to create
              your account for your agency — and you'll be able to invite the
              rest of your team.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-6" data-id="element-38">
              <Mail size={14} className="text-zano-pink" data-id="element-39" />
              <span data-id="element-40">
                Sent to{' '}
                <span className="font-medium text-zano-charcoal" data-id="element-41">
                  {SUPPORT_EMAIL}
                </span>
              </span>
            </div>
            <Button variant="primary" className="w-full justify-center" onClick={onClose} data-id="element-42">
              Done
            </Button>
          </div> : <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-43">
            <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-44">
              List Your Agency
            </h3>
            <p className="text-gray-600 mb-6 text-sm" data-id="element-45">
              Join 400+ talent agencies on Zano. Tell us a little about you,
              your agency, and your creators to get started.
            </p>
            <form className="space-y-6" onSubmit={handleSubmit} data-id="element-46">
              {/* About you */}
              <div data-id="element-47">
                <div className="flex items-center gap-2 mb-3" data-id="element-48">
                  <div className="p-1.5 rounded-md bg-zano-blush text-zano-pink" data-id="element-49">
                    <User size={14} data-id="element-50" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-51">
                    About you
                  </h4>
                </div>
                <div className="space-y-4" data-id="element-52">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-53">
                    <div data-id="element-54">
                      <label className={labelClass} data-id="element-55">Full name</label>
                      <input name="fullName" type="text" required className={inputClass} data-id="element-56" />
                    </div>
                    <div data-id="element-57">
                      <label className={labelClass} data-id="element-58">Role / title</label>
                      <input name="role" type="text" placeholder="e.g. Talent Manager" className={inputClass} data-id="element-59" />
                    </div>
                  </div>
                  <div data-id="element-60">
                    <label className={labelClass} data-id="element-61">Email</label>
                    <input name="email" type="email" required className={inputClass} data-id="element-62" />
                  </div>
                  <div data-id="element-63">
                    <label className={labelClass} data-id="element-64">LinkedIn profile URL</label>
                    <input name="linkedin" type="url" placeholder="https://linkedin.com/in/you" className={inputClass} data-id="element-65" />
                  </div>
                </div>
              </div>

              {/* Your agency */}
              <div data-id="element-66">
                <div className="flex items-center gap-2 mb-3" data-id="element-67">
                  <div className="p-1.5 rounded-md bg-zano-blush text-zano-pink" data-id="element-68">
                    <Building2 size={14} data-id="element-69" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-70">
                    Your agency
                  </h4>
                </div>
                <div className="space-y-4" data-id="element-71">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-72">
                    <div data-id="element-73">
                      <label className={labelClass} data-id="element-74">Agency name</label>
                      <input name="agencyName" type="text" required className={inputClass} data-id="element-75" />
                    </div>
                    <div data-id="element-76">
                      <label className={labelClass} data-id="element-77">Website</label>
                      <input name="website" type="text" placeholder="agency.com" className={inputClass} data-id="element-78" />
                    </div>
                  </div>
                  <div data-id="element-79">
                    <label className={labelClass} data-id="element-80">Number of creators</label>
                    <select name="creators" className={inputClass} data-id="element-81">
                      <option data-id="element-82">1-10</option>
                      <option data-id="element-83">11-50</option>
                      <option data-id="element-84">51-100</option>
                      <option data-id="element-85">100+</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Your creators */}
              <div data-id="element-86">
                <div className="flex items-center gap-2 mb-3" data-id="element-87">
                  <div className="p-1.5 rounded-md bg-zano-blush text-zano-pink" data-id="element-88">
                    <Users size={14} data-id="element-89" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-90">
                    Your creators
                  </h4>
                </div>
                <div className="space-y-4" data-id="element-91">
                  <div data-id="element-92">
                    <label className={labelClass} data-id="element-93">
                      Roster link{' '}
                      <span className="text-gray-400 font-normal" data-id="element-94">
                        (optional)
                      </span>
                    </label>
                    <input name="rosterLink" type="text" placeholder="Link to your roster or media kit" className={inputClass} data-id="element-95" />
                  </div>
                  <div data-id="element-96">
                    <label className={labelClass} data-id="element-97">Primary niche</label>
                    <select name="niche" defaultValue="" className={inputClass} data-id="element-98">
                      <option value="" disabled data-id="element-99">
                        Select a niche
                      </option>
                      <option data-id="element-100">Fashion</option>
                      <option data-id="element-101">Beauty</option>
                      <option data-id="element-102">Lifestyle</option>
                      <option data-id="element-103">Tech</option>
                      <option data-id="element-104">Gaming</option>
                      <option data-id="element-105">Fitness</option>
                      <option data-id="element-106">Food</option>
                      <option data-id="element-107">Travel</option>
                      <option data-id="element-108">Mixed / Multiple</option>
                    </select>
                  </div>
                  <div data-id="element-109">
                    <label className={labelClass} data-id="element-110">
                      Anything else?{' '}
                      <span className="text-gray-400 font-normal" data-id="element-111">
                        (optional)
                      </span>
                    </label>
                    <textarea name="notes" rows={2} placeholder="Tell us more about your roster" className={inputClass} data-id="element-112" />
                  </div>
                </div>
              </div>

              {/* Referral */}
              <div data-id="element-113">
                <div className="space-y-4" data-id="element-114">
                  <div data-id="element-115">
                    <label className={labelClass} data-id="element-116">
                      Were you referred by an existing Zano user?
                    </label>
                    <select name="referred" value={referred} onChange={e => setReferred(e.target.value)} className={inputClass} data-id="element-117">
                      <option value="" disabled data-id="element-118">
                        Select an option
                      </option>
                      <option data-id="element-119">No</option>
                      <option data-id="element-120">Yes</option>
                    </select>
                  </div>
                  {referred === 'Yes' && <div data-id="element-121">
                      <label className={labelClass} data-id="element-122">Who referred you?</label>
                      <input name="referredBy" type="text" placeholder="Name of the Zano user who referred you" className={inputClass} data-id="element-123" />
                    </div>}
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-124">
                <input type="checkbox" id="tos" name="tos" required className="mt-1 shrink-0 rounded border-gray-300 text-zano-pink focus:ring-zano-pink" data-id="element-125" />
                <label htmlFor="tos" className="text-[11px] text-gray-500 leading-relaxed" data-id="element-126">
                  By checking this box, I acknowledge and agree to the Terms of
                  Service and Privacy Policy by Zano Platform. I acknowledge
                  that Zano Platform may collect, process, and store my personal
                  data provided in this form for the purpose of evaluating my
                  request and providing access to the platform. I understand
                  that my data will be handled in accordance with the Zano
                  Privacy Policy and GDPR regulations.
                </label>
              </div>

              {error && <p className="text-sm text-red-600" role="alert" data-id="element-127">
                  {error}
                </p>}
              <Button variant="primary" className="w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed" type="submit" disabled={submitting} data-id="element-128">
                {submitting ? 'Sending…' : 'Submit'}
              </Button>
            </form>
          </div>}
      </div>
    </div>;
};